import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { IdCard, Loader2, CheckCircle2, XCircle, Timer, ChevronRight, LogOut, RotateCcw, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-dwc-charcoal outline-none focus:border-dwc-green";
const LANG_LABELS = { en: "English", hi: "हिन्दी", gu: "ગુજરાતી" };
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const Card = ({ children, testId }) => (
  <div className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-[0_16px_50px_rgb(0,0,0,0.06)] md:p-10" data-testid={testId}>{children}</div>
);

const Login = ({ onLogin }) => {
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const req = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/ll/auth/request-otp`, { mobile });
      if (r.data.dev_otp) setDevOtp(r.data.dev_otp);
      if (r.data.note) setNote(r.data.note);
      setStep("otp");
      toast.success("OTP sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send OTP"); }
    setLoading(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/ll/auth/verify-otp`, { mobile, otp, name });
      onLogin(r.data.token, r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid OTP"); }
    setLoading(false);
  };
  return (
    <Card testId="ll-login-card">
      <IdCard className="h-10 w-10 text-dwc-green" aria-hidden="true" />
      <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-dwc-charcoal">Sign in to start preparing</h2>
      <p className="mt-1 text-sm text-dwc-charcoal/60">Verify your mobile number with a one-time password.</p>
      {step === "mobile" ? (
        <form onSubmit={req} className="mt-6 space-y-3">
          <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name" aria-label="Name" data-testid="ll-name-input" className={inputCls} />
          <input required minLength={10} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile Number" aria-label="Mobile number" data-testid="ll-mobile-input" className={inputCls} />
          <button disabled={loading} data-testid="ll-request-otp-btn" className="w-full rounded-xl bg-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark disabled:opacity-60">
            {loading ? "Sending..." : "Send OTP"}
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="mt-6 space-y-3">
          {devOtp && (
            <p className="rounded-lg bg-dwc-soft px-4 py-3 text-[12px] font-bold text-dwc-green" data-testid="ll-dev-otp-note">
              {note || "Your one-time password is shown below for quick sign-in."} Your OTP is: <span className="font-black">{devOtp}</span>
            </p>
          )}
          <input required value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter 6-digit OTP" aria-label="OTP" data-testid="ll-otp-input" className={inputCls} />
          <button disabled={loading} data-testid="ll-verify-otp-btn" className="w-full rounded-xl bg-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark disabled:opacity-60">
            {loading ? "Verifying..." : "Verify & Continue"}
          </button>
          <button type="button" onClick={() => setStep("mobile")} className="w-full text-center text-[12px] font-bold uppercase tracking-wide text-dwc-charcoal/50">Change number</button>
        </form>
      )}
    </Card>
  );
};

const Question = ({ q, index, total, selected, onSelect, showAnswer }) => (
  <div data-testid={`ll-question-${index}`}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-dwc-green">Question {index + 1} of {total}</p>
    <h3 className="mt-2 text-lg font-extrabold leading-snug text-dwc-charcoal">{q.question}</h3>
      {q.image && <img src={q.image} alt="Question" loading="lazy" className="mt-4 w-full max-w-xl rounded-xl border border-gray-100 bg-white object-contain" />}
    <div className="mt-5 space-y-2.5">
      {q.options.map((opt, oi) => {
        const isImg = typeof opt === "string" && opt.startsWith("/llq/");
        let cls = "border-gray-200 hover:border-dwc-green/50";
        if (showAnswer && oi === q.correct_index) cls = "border-dwc-green bg-dwc-soft";
        else if (showAnswer && selected === oi) cls = "border-dwc-crimson bg-dwc-crimson/[0.05]";
        else if (selected === oi) cls = "border-dwc-green bg-dwc-soft";
        return (
          <button key={oi} onClick={() => onSelect(oi)} disabled={showAnswer} data-testid={`ll-option-${oi}`}
            className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left text-sm font-bold text-dwc-charcoal transition-colors ${cls}`}>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-dwc-charcoal/5 text-xs font-black">{String.fromCharCode(65 + oi)}</span>
            {isImg ? <img src={opt} alt={`Option ${oi + 1}`} loading="lazy" className="h-14 min-w-0 flex-1 object-contain object-left sm:h-16" /> : opt}
            {showAnswer && oi === q.correct_index && <CheckCircle2 className="ml-auto h-5 w-5 shrink-0 text-dwc-green" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
    {showAnswer && q.explanation && <p className="mt-4 rounded-xl bg-dwc-soft p-4 text-sm text-dwc-charcoal/75" data-testid="ll-explanation">{q.explanation}</p>}
  </div>
);

const Practice = ({ token, language, onBack }) => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const loadingMore = useRef(false);

  const loadMore = useCallback((skip) => {
    if (loadingMore.current) return;
    loadingMore.current = true;
    setLoadError(false);
    axios.get(`${API}/api/ll/questions?language=${language}&skip=${skip}&limit=20`, auth(token))
      .then((r) => setData((prev) => ({ total: r.data.total, questions: [...(skip === 0 ? [] : prev?.questions || []), ...r.data.questions] })))
      .catch(() => setLoadError(true))
      .finally(() => { loadingMore.current = false; });
  }, [token, language]);

  useEffect(() => { setData(null); setIdx(0); setSelected(null); loadMore(0); }, [loadMore]);

  const retryCard = (skip) => (
    <Card testId="ll-practice-error">
      <p className="text-sm font-bold text-dwc-charcoal">Could not load questions. Please check your connection.</p>
      <button onClick={() => loadMore(skip)} data-testid="ll-practice-retry-btn" className="mt-4 rounded-xl bg-dwc-green px-5 py-3 text-sm font-bold uppercase tracking-wide text-white">Retry</button>
    </Card>
  );
  if (!data) return loadError ? retryCard(0) : <Card><Loader2 className="mx-auto h-8 w-8 animate-spin text-dwc-green" aria-hidden="true" /></Card>;
  if (data.total === 0)
    return (
      <Card testId="ll-practice-empty">
        <h3 className="text-xl font-extrabold text-dwc-charcoal">Question bank coming soon</h3>
        <p className="mt-2 text-sm text-dwc-charcoal/65">The official {LANG_LABELS[language]} question bank is being loaded by the DWC team. Please check back shortly.</p>
        <button onClick={onBack} className="mt-5 rounded-xl border-2 border-dwc-green px-5 py-3 text-sm font-bold uppercase tracking-wide text-dwc-green">Back</button>
      </Card>
    );
  const q = data.questions[idx];
  if (!q) return loadError ? retryCard(data.questions.length) : <Card><Loader2 className="mx-auto h-8 w-8 animate-spin text-dwc-green" aria-hidden="true" /></Card>;
  const goNext = () => {
    setSelected(null);
    const next = idx + 1;
    if (next >= data.questions.length) {
      if (data.questions.length < data.total) { loadMore(data.questions.length); setIdx(next); }
      else setIdx(0);
    } else {
      setIdx(next);
      if (data.questions.length < data.total && next >= data.questions.length - 5) loadMore(data.questions.length);
    }
  };
  return (
    <Card testId="ll-practice-card">
      <Question q={q} index={idx} total={data.total} selected={selected} onSelect={setSelected} showAnswer={selected !== null} />
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="text-[12px] font-bold uppercase tracking-wide text-dwc-charcoal/50">Exit Practice</button>
        <button
          data-testid="ll-practice-next-btn"
          onClick={goNext}
          className="inline-flex items-center gap-1.5 rounded-xl bg-dwc-green px-5 py-3 text-sm font-bold uppercase tracking-wide text-white"
        >
          Next <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </Card>
  );
};

const Exam = ({ token, language, onDone, onBack }) => {
  const [exam, setExam] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const submitting = useRef(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    axios.post(`${API}/api/ll/exam/start`, { language }, auth(token))
      .then((r) => { setExam(r.data); setTimeLeft(r.data.seconds_per_question); })
      .catch((e) => setErr(e.response?.data?.detail || "Could not start the exam"));
  }, [token, language]);

  const finish = useCallback(async (finalAnswers) => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      const r = await axios.post(`${API}/api/ll/exam/submit`, { attempt_id: exam.attempt_id, answers: finalAnswers }, auth(token));
      onDone(r.data);
    } catch { toast.error("Could not submit exam"); submitting.current = false; }
  }, [exam, token, onDone]);

  const next = useCallback((ans) => {
    if (!exam) return;
    if (idx + 1 >= exam.questions.length) finish(ans);
    else { setIdx((i) => i + 1); setTimeLeft(exam.seconds_per_question); }
  }, [exam, idx, finish]);

  useEffect(() => {
    if (!exam) return;
    const t = setInterval(() => {
      setTimeLeft((s) => {
        if (s <= 1) { next(answersRef.current); return exam.seconds_per_question; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [exam, idx, next]);

  const answersRef = useRef(answers);
  answersRef.current = answers;

  if (err)
    return (
      <Card testId="ll-exam-error">
        <XCircle className="h-8 w-8 text-dwc-crimson" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-dwc-charcoal">{err}</p>
        <button onClick={onBack} className="mt-5 rounded-xl border-2 border-dwc-green px-5 py-3 text-sm font-bold uppercase tracking-wide text-dwc-green">Back</button>
      </Card>
    );
  if (!exam) return <Card><Loader2 className="mx-auto h-8 w-8 animate-spin text-dwc-green" aria-hidden="true" /></Card>;
  const q = exam.questions[idx];
  return (
    <Card testId="ll-exam-card">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-dwc-charcoal/50">DWC Mock Exam · Attempt #{exam.attempt_no}</p>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-black ${timeLeft <= 10 ? "bg-dwc-crimson/10 text-dwc-crimson" : "bg-dwc-soft text-dwc-green"}`} data-testid="ll-exam-timer">
          <Timer className="h-4 w-4" aria-hidden="true" /> {timeLeft}s
        </span>
      </div>
      <Question
        q={q} index={idx} total={exam.questions.length}
        selected={answers[q.id] ?? null}
        onSelect={(oi) => setAnswers((a) => ({ ...a, [q.id]: oi }))}
        showAnswer={false}
      />
      <button
        data-testid="ll-exam-next-btn"
        onClick={() => next(answersRef.current)}
        className="mt-6 w-full rounded-xl bg-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark"
      >
        {idx + 1 >= exam.questions.length ? "Submit Exam" : "Next Question"}
      </button>
    </Card>
  );
};

const Certificate = ({ result }) => {
  const [img, setImg] = useState("");
  const certId = (result.attempt_id || "").slice(0, 8).toUpperCase();
  const date = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    const c = document.createElement("canvas");
    c.width = 1200;
    c.height = 840;
    const x = c.getContext("2d");
    x.fillStyle = "#FFFFFF";
    x.fillRect(0, 0, 1200, 840);
    x.fillStyle = "#F2F8F4";
    x.fillRect(0, 0, 1200, 110);
    x.strokeStyle = "#176B3A";
    x.lineWidth = 12;
    x.strokeRect(26, 26, 1148, 788);
    x.strokeStyle = "#B5122B";
    x.lineWidth = 2;
    x.strokeRect(48, 48, 1104, 744);
    x.textAlign = "center";
    x.fillStyle = "#176B3A";
    x.font = "900 40px Arial";
    x.fillText("DRIVE WITH CONFIDENCE", 600, 145);
    x.fillStyle = "#B5122B";
    x.font = "bold 18px Arial";
    x.fillText("WHERE PSYCHOLOGY MEETS THE ROAD · SURAT", 600, 182);
    x.fillStyle = "#1F2933";
    x.font = "bold 54px Georgia";
    x.fillText("Certificate of Achievement", 600, 290);
    x.fillStyle = "#666";
    x.font = "24px Arial";
    x.fillText("This certifies that", 600, 360);
    x.fillStyle = "#1F2933";
    x.font = "bold 46px Georgia";
    x.fillText(result.name || "DWC Student", 600, 425);
    x.strokeStyle = "#176B3A";
    x.lineWidth = 2;
    x.beginPath();
    x.moveTo(380, 445);
    x.lineTo(820, 445);
    x.stroke();
    x.fillStyle = "#666";
    x.font = "24px Arial";
    x.fillText("has successfully passed the DWC Learning Licence Mock Exam", 600, 500);
    x.fillStyle = "#176B3A";
    x.font = "900 40px Arial";
    x.fillText(`Score: ${result.score} / ${result.total}`, 600, 575);
    x.fillStyle = "#666";
    x.font = "20px Arial";
    x.fillText(`Date: ${date}   ·   Certificate ID: DWC-${certId}`, 600, 635);
    x.fillStyle = "#999";
    x.font = "15px Arial";
    x.fillText("Practice mock exam certificate — not an official Government of India / RTO document.", 600, 755);
    setImg(c.toDataURL("image/png"));
  }, [result, certId, date]);

  const shareText = encodeURIComponent(
    `I passed the DWC Learning Licence Mock Exam with ${result.score}/${result.total}! 🎉 Prepare for your Learning Licence with Drive With Confidence — Mumbai, Surat & Ahmedabad: ${window.location.origin}/learning-licence-prep`
  );

  if (!img) return null;
  return (
    <div className="mt-8 rounded-2xl border border-dwc-green/25 bg-dwc-soft p-4 sm:p-5" data-testid="ll-certificate-section">
      <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-dwc-green">Your DWC Pass Certificate</p>
      <img src={img} alt="DWC Learning Licence Mock Exam Pass Certificate" className="mt-3 w-full rounded-xl border border-gray-200 shadow-sm" data-testid="ll-certificate-image" />
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <a
          href={img}
          download={`DWC-Certificate-${certId}.png`}
          data-testid="ll-certificate-download-btn"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-dwc-green px-5 py-3 text-sm font-bold uppercase tracking-wide text-white"
        >
          <Download className="h-4 w-4" aria-hidden="true" /> Download Certificate
        </a>
        <a
          href={`https://wa.me/?text=${shareText}`}
          target="_blank"
          rel="noreferrer"
          data-testid="ll-certificate-share-btn"
          className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dwc-green px-5 py-3 text-sm font-bold uppercase tracking-wide text-dwc-green"
        >
          <Share2 className="h-4 w-4" aria-hidden="true" /> Share on WhatsApp
        </a>
      </div>
    </div>
  );
};

const Result = ({ result, onRetry, onBack }) => (
  <Card testId="ll-result-card">
    {result.passed ? <CheckCircle2 className="mx-auto h-16 w-16 text-dwc-green" aria-hidden="true" /> : <XCircle className="mx-auto h-16 w-16 text-dwc-crimson" aria-hidden="true" />}
    <h2 className="mt-4 text-center text-4xl font-black tracking-tight text-dwc-charcoal" data-testid="ll-result-score">{result.score} / {result.total}</h2>
    <p className={`mt-2 text-center text-2xl font-extrabold uppercase tracking-[0.2em] ${result.passed ? "text-dwc-green" : "text-dwc-crimson"}`} data-testid="ll-result-status">
      {result.passed ? "Pass" : "Fail"}
    </p>
    <p className="mt-2 text-center text-sm text-dwc-charcoal/60">
      Passing requirement: {result.pass_score} correct · Attempt #{result.attempt_no} · {LANG_LABELS[result.language]}
    </p>
    <p className="mt-2 text-center text-[11px] text-dwc-charcoal/45">This is the DWC Learning Licence practice mock exam — not an official government result.</p>
    {result.passed && <Certificate result={result} />}
    <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
      <button onClick={onRetry} data-testid="ll-retry-btn" className="inline-flex items-center justify-center gap-2 rounded-xl bg-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Retry New Exam</button>
      <button onClick={onBack} className="rounded-xl border-2 border-dwc-green px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-dwc-green">Back to Preparation</button>
    </div>
    <div className="mt-8 space-y-4">
      {result.review.map((r, i) => (
        <div key={r.id} className={`rounded-xl border p-4 ${r.correct ? "border-dwc-green/25 bg-dwc-soft" : "border-dwc-crimson/25 bg-dwc-crimson/[0.03]"}`}>
          <p className="text-sm font-bold text-dwc-charcoal">{i + 1}. {r.question}</p>
          {r.image && r.image.startsWith("/llq/") && <img src={r.image} alt="Question" loading="lazy" className="mt-2 max-w-md rounded-lg border border-gray-100 bg-white" />}
          <div className="mt-1.5 text-[13px] font-semibold text-dwc-charcoal/70">
            <span className="text-dwc-green">Correct: {String.fromCharCode(65 + r.correct_index)}</span>
            {!r.correct && <span className="ml-2 text-dwc-crimson">Your answer: {r.selected != null ? String.fromCharCode(65 + r.selected) : "Not answered"}</span>}
          </div>
        </div>
      ))}
    </div>
  </Card>
);

const LicencePrep = () => {
  const [token, setToken] = useState(() => localStorage.getItem("dwc-ll-token") || "");
  const [language, setLanguage] = useState("");
  const [mode, setMode] = useState("home");
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/api/ll/settings`).then((r) => setCounts(r.data.question_counts || {})).catch(() => {});
  }, []);
  useEffect(() => {
    if (token && mode === "home") axios.get(`${API}/api/ll/attempts`, auth(token)).then((r) => setAttempts(r.data)).catch((e) => { if (e.response?.status === 401) logout(); });
  }, [token, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => { localStorage.removeItem("dwc-ll-token"); setToken(""); setMode("home"); };

  return (
    <section className="min-h-screen bg-dwc-soft pb-24 pt-32 md:pt-40" data-testid="ll-prep-page">
      <div className="mx-auto max-w-2xl px-5">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-dwc-green">DWC Learning Licence</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-dwc-charcoal sm:text-4xl">Learning Licence Preparation</h1>
        <p className="mt-3 text-sm leading-relaxed text-dwc-charcoal/65">
          Practise official Transport Department style questions and take randomized mock exams — in English, Hindi or Gujarati. This is DWC's practice platform, not the official government exam.
        </p>
        <div className="mt-8">
          {!token ? (
            <Login onLogin={(t) => { localStorage.setItem("dwc-ll-token", t); setToken(t); }} />
          ) : mode === "practice" ? (
            <Practice token={token} language={language} onBack={() => setMode("home")} />
          ) : mode === "exam" ? (
            <Exam token={token} language={language} onDone={(r) => { setResult(r); setMode("result"); }} onBack={() => setMode("home")} />
          ) : mode === "result" ? (
            <Result result={result} onRetry={() => setMode("exam")} onBack={() => setMode("home")} />
          ) : (
            <Card testId="ll-home-card">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold text-dwc-charcoal">Select Language</h2>
                <button onClick={logout} data-testid="ll-logout-btn" className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-dwc-charcoal/50 hover:text-dwc-crimson"><LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Logout</button>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {Object.entries(LANG_LABELS).map(([code, label]) => (
                  <button key={code} onClick={() => setLanguage(code)} data-testid={`ll-lang-${code}`}
                    className={`rounded-xl border-2 py-4 text-sm font-extrabold transition-colors ${language === code ? "border-dwc-green bg-dwc-soft text-dwc-green" : "border-gray-200 text-dwc-charcoal/70"}`}>
                    {label}
                    <span className="mt-1 block text-[10px] font-bold text-dwc-charcoal/40">{counts[code] ?? 0} questions</span>
                  </button>
                ))}
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {!language && <p className="text-xs font-bold text-dwc-charcoal/50 sm:col-span-2" data-testid="ll-lang-hint">Select a language above to unlock Practice Mode and the Mock Exam.</p>}
                <button disabled={!language} onClick={() => setMode("practice")} data-testid="ll-start-practice-btn"
                  className="rounded-xl border-2 border-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-colors hover:bg-dwc-soft disabled:opacity-40">
                  Practice Mode
                </button>
                <button disabled={!language} onClick={() => setMode("exam")} data-testid="ll-start-exam-btn"
                  className="rounded-xl bg-dwc-green py-4 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-dwc-green-dark disabled:opacity-40">
                  Start Mock Exam
                </button>
              </div>
              <p className="mt-4 text-center text-[11px] text-dwc-charcoal/50">Mock exam: 15 random questions · 45 seconds each · 11 correct to pass (DWC practice configuration)</p>
              {attempts.length > 0 && (
                <div className="mt-6 border-t border-gray-100 pt-5" data-testid="ll-attempts-list">
                  <h3 className="text-xs font-extrabold uppercase tracking-wide text-dwc-charcoal/50">Your Attempts</h3>
                  <div className="mt-3 space-y-2">
                    {attempts.slice(0, 5).map((a) => (
                      <p key={a.id} className="flex justify-between rounded-lg bg-dwc-soft px-4 py-2.5 text-[13px] font-bold text-dwc-charcoal">
                        <span>#{a.attempt_no} · {LANG_LABELS[a.language]}</span>
                        <span className={a.passed ? "text-dwc-green" : "text-dwc-crimson"}>{a.score}/{a.total} · {a.passed ? "PASS" : "FAIL"}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};

export default LicencePrep;
